import { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';

const TaxRequestModal = ({
  open,
  onClose,
  taxRequestData,
  onApproveReject,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.put(`/admin/user/approve-tax-request?userId=${taxRequestData?.userId}`);

      if (response?.status === 200) {
        toast.success('Tax request approved successfully');
        onApproveReject();
        onClose();
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Failed to approve tax request';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.put(`/admin/user/reject-tax-request?userId=${taxRequestData?.userId}`);
      console.log('Reject response:', response);

      if (response?.status === 200) {
        toast.success('Tax request rejected successfully');
        onApproveReject();
        onClose();
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 'Failed to reject tax request';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1a1a1a',
          borderBottom: '1px solid #eee',
          pb: 2,
        }}
      >
        Tax Request Review
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: '8px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              '& .MuiAlert-icon': {
                color: '#c62828',
              },
            }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ py: 2 }}>
          {/* User Info */}
          <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #eee' }}>
            <Typography
              variant="subtitle2"
              sx={{ color: '#999', mb: 0.8, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              User ID
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
              #{taxRequestData?.userId}
            </Typography>
          </Box>

          {/* Old Value */}
          <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #eee' }}>
            <Typography
              variant="subtitle2"
              sx={{ color: '#999', mb: 1, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Current Tax Rate
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '32px',
                color: '#1976d2',
              }}
            >
              {taxRequestData?.taxRequestOldValue
                ? `${taxRequestData.taxRequestOldValue}%`
                : 'N/A'}
            </Typography>
          </Box>

          {/* New Value */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: '#999', mb: 1, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Requested Tax Rate
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '32px',
                color: '#2e7d32',
                padding: '16px',
                backgroundColor: '#e8f5e9',
                borderRadius: '10px',
                borderLeft: '5px solid #2e7d32',
                display: 'inline-block',
              }}
            >
              {taxRequestData?.taxRequestNewValue
                ? `${taxRequestData?.taxRequestNewValue}%`
                : 'N/A'}
            </Typography>
          </Box>

          {/* Reason if provided */}
          {taxRequestData?.reason && (
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #eee' }}>
              <Typography
                variant="subtitle2"
                sx={{ color: '#999', mb: 0.8, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                Reason for Change
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                {taxRequestData.reason}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            color: '#666',
            borderColor: '#d0d0d0',
            textTransform: 'capitalize',
            fontWeight: 500,
            fontSize: '14px',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: '#f5f5f5',
            },
            '&:disabled': {
              color: '#999',
              borderColor: '#e0e0e0',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleReject}
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: '110px',
            textTransform: 'capitalize',
            fontWeight: 600,
            fontSize: '14px',
            backgroundColor: '#ff4842',
            '&:hover': {
              backgroundColor: '#b71c1c',
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#999',
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Reject'}
        </Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: '110px',
            textTransform: 'capitalize',
            fontWeight: 600,
            fontSize: '14px',
            backgroundColor: '#62b5e5',
            '&:hover': {
              backgroundColor: '#1565c0',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#999',
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxRequestModal;
